
<app-step-dialog-shell
  [controller]="this"
  mode="sequential"
  [title]="dialogTitle"
  [subtitle]="dialogSubtitle"
  [showClose]="true"
  [disableClose]="isSavingTeam"
  (closeClicked)="close()"
  (cancelClicked)="close()"
  (primaryClicked)="save()"
>
  <ng-template [stepDialogStep]="0">
    <div class="field">
      <label class="field-label">Team name</label>
      <mat-form-field appearance="outline" class="full">
        <input matInput [formControl]="form.controls.teamName" />
        <mat-error *ngIf="form.controls.teamName.hasError('required')">Team name is required</mat-error>
      </mat-form-field>
    </div>

    <div class="field">
      <label class="field-label">Wellness benefit amount ($)</label>
      <mat-form-field appearance="outline" class="amount">
        <span matTextPrefix>$&nbsp;</span>
        <input
          matInput
          type="number"
          min="0"
          [max]="wellnessAmountMax"
          step="0.01"
          inputmode="decimal"
          [formControl]="form.controls.wellnessAmount"
          (input)="onWellnessAmountInput($event)"
        />
        <mat-error *ngIf="form.controls.wellnessAmount.hasError('required')">Amount is required</mat-error>
        <mat-error *ngIf="form.controls.wellnessAmount.hasError('max')">
          Amount cannot exceed $999,999.99
        </mat-error>
      </mat-form-field>

      <div class="helper">Monthly company-funded benefit amount per employee</div>
    </div>
  </ng-template>

  <ng-template [stepDialogStep]="1">
    <div class="step2">
      <div class="payroll-connection-error" *ngIf="hasMembersConnectionError">
        <div class="error-content">
          <mat-icon>error_outline</mat-icon>
          <div class="error-copy">
            <div class="error-title">Payroll connection error</div>
            <div class="error-message">{{ membersConnectionError }}</div>
          </div>
        </div>
        <button mat-raised-button color="primary" type="button" (click)="goToCompanyConnections()">
          Go to Company Connections
        </button>
      </div>

      <ng-container *ngIf="!hasMembersConnectionError">
      <div class="payroll-fallback-banner" *ngIf="fallbackBannerMessage">
        {{ fallbackBannerMessage }}
      </div>
      <mat-expansion-panel
        class="manual-management-panel"
        *ngIf="canShowManualMemberManagement"
        [(expanded)]="isManualMemberManagementExpanded"
      >
        <mat-expansion-panel-header>
          <mat-panel-title>
            Manual Member Management
          </mat-panel-title>
          <mat-panel-description>
            {{ data.team.name || 'Current team' }}
          </mat-panel-description>
        </mat-expansion-panel-header>

        <div class="manual-management">
          <div class="manual-management__actions">
            <mat-form-field appearance="outline" class="manual-management__email">
              <mat-label>Email address</mat-label>
              <input
                matInput
                type="email"
                [(ngModel)]="manualMemberEmail"
                placeholder="name@company.com"
                (keyup.enter)="onAddFallbackTeamMember()"
              />
            </mat-form-field>

            <button
              mat-raised-button
              color="primary"
              type="button"
              [disabled]="!manualMemberEmail.trim() || isAddingManualMember"
              (click)="onAddFallbackTeamMember()"
            >
              Add by email
            </button>
          </div>

          <div class="manual-management__upload">
            <div class="manual-management__upload-copy">
              Add members to <strong>{{ data.team.name || 'this team' }}</strong> with a CSV or XLSX file.
              Supported column headers are
              <ng-container *ngFor="let column of teamMemberUploadColumns; let last = last">
                <strong>{{ column }}</strong><span *ngIf="!last">, </span>
              </ng-container>.
              <ng-container *ngFor="let column of requiredUploadColumns; let last = last">
                <strong>{{ column }}</strong><span *ngIf="!last">, </span>
              </ng-container>
              {{ requiredUploadColumnVerb }} required.
              <a target="_blank" [href]="uploadTemplateUrl">Download template</a>
            </div>
            <app-fileupload
              requiredFileType=".csv,.xlsx"
              (doneEvent)="onUploadFallbackTeamMembers($event)"
            ></app-fileupload>
          </div>

          <div class="manual-management__result" *ngIf="manualMemberUploadResult">
            <span *ngIf="manualMemberUploadResult.newCount !== undefined">
              {{ manualMemberUploadResult.newCount }} members added.
            </span>
            <span *ngIf="manualMemberUploadResult.errorMessage">
              {{ manualMemberUploadResult.errorMessage }}
            </span>
          </div>
        </div>
      </mat-expansion-panel>
      <div class="step2-header">
        <div class="step2-title">{{ canUseFilterDrivenMembersStep ? 'FILTER EMPLOYEE LIST' : 'SEARCH EMPLOYEE LIST' }}</div>

        <!-- Search -->
        <mat-form-field appearance="outline" class="search">
          <mat-icon matPrefix>search</mat-icon>
          <input
            matInput
            placeholder="Search employees by name or email..."
            [(ngModel)]="search"
          />
        </mat-form-field>

        <!-- Filter buttons row -->
        <div class="filter-row" *ngIf="canUseFilterDrivenMembersStep">
          <mat-form-field appearance="outline" class="filter" *ngFor="let filter of availableFilters">
            <mat-select
              class="payroll-filter-select"
              [placeholder]="filter[0]"
              [value]="activeFiltersMap[filter[0]]"
              (selectionChange)="handleFilterSelect(filter[0], $event.value)"
              panelClass="payroll-filter-select-panel"
              multiple
            >
              <mat-select-trigger>
                <span class="filter-trigger">
                  <span class="filter-trigger-label">{{ filter[0] }}</span>
                  <span class="filter-trigger-count">{{ activeFilterCount(filter[0]) }}</span>
                </span>
              </mat-select-trigger>
              <mat-option *ngFor="let filterOption of filter[1]" [value]="filterOption">
                {{ displayFilterValue(filter[0], filterOption) }}
              </mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <!-- Applied chips + Clear all -->
        <div class="applied-row" *ngIf="canUseFilterDrivenMembersStep && activeFilters.length">
          <mat-chip-listbox class="chips">
            <mat-chip-option
              *ngFor="let f of activeFilters"
              selected
              removable
              (removed)="clearFilter(f.key, f.value)"
            >
              <span class="chip-label">{{ f.label }}: {{ displayFilterValue(f.label, f.value) }}</span>
              <button matChipRemove class="chip-x" aria-label="Remove filter">
                <mat-icon>close</mat-icon>
              </button>
            </mat-chip-option>
          </mat-chip-listbox>

          <button mat-button class="clear-all" (click)="clearAllFilters()">Clear all</button>
        </div>

        <!-- Auto-add row -->
        <div class="autoadd-row" *ngIf="canShowAutoAddNewMembersControl">
          <mat-checkbox [(ngModel)]="autoAddNewMembers" [disabled]="!canEnableAutoAddNewMembers">
            Auto-add new Team Members
          </mat-checkbox>
          <button
            mat-icon-button
            [matTooltip]="autoAddNewMembersTooltip"
            aria-label="Info"
          >
            <mat-icon>help_outline</mat-icon>
          </button>
        </div>
        <div class="autoadd-helper" *ngIf="canShowAutoAddNewMembersControl && !canEnableAutoAddNewMembers">
          {{ autoAddNewMembersDisabledReason }}
        </div>
      </div>

      <!-- List header row -->
      <div
        class="employee-results-wrap"
        [class.is-loading]="isEmployeeResultsLoading"
        [attr.aria-busy]="isEmployeeResultsLoading"
      >
        <div class="results-loading-overlay" *ngIf="isEmployeeResultsLoading" aria-live="polite">
          <mat-spinner [diameter]="30"></mat-spinner>
          <div class="results-loading-copy">Applying filters...</div>
        </div>

        <div class="list-meta">
          <div class="left">
            <mat-checkbox
              class="row-check"
              [checked]="allVisibleSelected"
              [indeterminate]="someVisibleSelected"
              (change)="toggleAllVisible($event.checked)"
              [disabled]="isEmployeeResultsLoading"
              aria-label="Select all visible employees"
            ></mat-checkbox>
            <span>Showing <b>{{ filteredEmployees.length }}</b> employees</span>
          </div>

          <div class="right">
            <span class="selected-pill">
              {{ selected.size }} selected
            </span>
          </div>
        </div>

        <mat-divider></mat-divider>

        <!-- Employee list -->
        <div class="employee-list">
          <div
            class="employee-row"
            *ngFor="let e of filteredEmployees"
            [class.selected]="isEmployeeSelected(e)"
          >
            <mat-checkbox
              class="row-check"
              [checked]="isEmployeeSelected(e)"
              (change)="toggleEmployee(employeeKey(e), $event.checked)"
              [disabled]="isEmployeeResultsLoading || !hasEmployeeEmail(e)"
              [matTooltip]="missingEmployeeEmailTooltip(e)"
            ></mat-checkbox>

            <div class="emp-main">
              <div class="emp-name">{{ e['First Name'] || e?.firstName }} {{ e['Last Name'] || e?.lastName }}</div>
              <div
                class="missing-email-alert"
                *ngIf="!hasEmployeeEmail(e); else employeeEmailLine"
                [matTooltip]="missingEmployeeEmailTooltip(e)"
              >
                <mat-icon>warning_amber</mat-icon>
                <span>Missing email address. Cannot import to Wallit.</span>
              </div>
              <ng-template #employeeEmailLine>
                <div class="emp-email">{{ employeeEmail(e) }}</div>
              </ng-template>
              <div class="emp-sub" *ngIf="employeeFilterSummary(e) as filterSummary">
                {{ filterSummary }}
              </div>
            </div>
          </div>
        </div>
      </div>
      </ng-container>
    </div>
  </ng-template>

  <!-- FOOTER: STEP 1 -->
  <ng-template [stepDialogFooter]="0">
    <button mat-stroked-button (click)="close()" [disabled]="isSavingTeam">Cancel</button>
    <button
      *ngIf="hasMembersStep; else createFromSelectionSaveButton"
      mat-raised-button
      color="primary"
      (click)="nextFromDetails()"
      [disabled]="isSavingTeam"
    >
      Next: Edit members
      <mat-icon class="btn-icon">chevron_right</mat-icon>
    </button>
    <ng-template #createFromSelectionSaveButton>
      <button mat-raised-button color="primary" (click)="save()" [disabled]="isSavingTeam">
        {{ saveButtonLabel }}
      </button>
    </ng-template>
  </ng-template>

  <!-- FOOTER: STEP 2 -->
  <ng-template [stepDialogFooter]="1">
    <button mat-stroked-button (click)="backToDetails()" [disabled]="isSavingTeam">
      <mat-icon class="btn-icon-left">chevron_left</mat-icon>
      Back
    </button>

    <button mat-stroked-button (click)="close()" [disabled]="isSavingTeam">Cancel</button>
    <button mat-raised-button color="primary" (click)="save()" [disabled]="isSavingTeam">
      {{ saveButtonLabel }}
    </button>
  </ng-template>
</app-step-dialog-shell>
